import { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardActionArea, Typography, Grid, Stack, Chip, Avatar,
  CircularProgress, Alert, Button, Divider, Tooltip,
} from '@mui/material';
import {
  Business as SchoolIcon,
  Login as EnterIcon,
  Home as HomeIcon,
  CheckCircle as CurrentIcon,
  Image as LogoIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import { useAuth } from '../../context/AuthContext';

// ─────────────────────────────────────────────────────────────────
// School Hub — pick a school and work in it.
//
// One card per school. Click a card and you are taken straight into
// that school: every page from then on shows that school's students,
// fees, payroll and reports. The main school is always shown first and
// there is a permanent way back to it, so nobody can get stranded
// inside a school they only meant to look at.
//
// Only visible to someone who may open other schools — an
// "All schools" user, or a super administrator of the main school. A
// super administrator of another school never sees this page; they run
// their own school and nothing else.
//
// Switching reloads the app on purpose (see AuthContext.switchSchool):
// every screen keeps its own fetched rows in component state, so a
// hard reload is the only way to be certain none of the previous
// school's figures linger.
// ─────────────────────────────────────────────────────────────────
export default function SchoolHubPage() {
  const { currentSchool, switchSchool } = useAuth();
  const [schools, setSchools] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [mainId, setMainId] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await API.get('/schools/accessible');
      setSchools(res.data?.schools || []);
      setCurrentId(res.data?.current_school_id ?? null);
      setMainId(res.data?.main_school_id ?? 1);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inMain = String(currentId) === String(mainId);

  if (loading) {
    return <Box sx={{ p: 8, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Avatar sx={{ bgcolor: 'primary.main' }}><SchoolIcon /></Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={800}>School Hub</Typography>
          <Typography variant="caption" color="text.secondary">
            Choose a school to work in. You can come back here at any time.
          </Typography>
        </Box>
        {!inMain && (
          <Button
            variant="outlined" startIcon={<HomeIcon />}
            onClick={() => switchSchool(mainId)}
          >
            Back to Main School
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

      {!inMain && (
        <Alert severity="warning" sx={{ my: 2 }}>
          You are currently working in <strong>{currentSchool?.name}</strong>.
          Everything you add or change is saved to that school.
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      {!schools.length ? (
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            No other schools yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Once a school request is approved it appears here, ready to open.
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {schools.map((s) => {
            const isCurrent = String(s.id) === String(currentId);
            const isMain    = String(s.id) === String(mainId);
            return (
              <Grid item xs={12} sm={6} md={4} key={s.id}>
                <Card sx={{
                  height: '100%', borderRadius: 2,
                  border: '2px solid',
                  borderColor: isCurrent ? 'primary.main' : 'divider',
                  boxShadow: isCurrent ? 4 : 1,
                  transition: 'transform .15s, box-shadow .15s',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: 5 },
                }}>
                  <Tooltip title={isCurrent ? 'You are already in this school' : `Open ${s.name}`}>
                    <CardActionArea
                      onClick={() => { if (!isCurrent) switchSchool(s.id); }}
                      sx={{ p: 2.2, height: '100%', alignItems: 'flex-start' }}
                    >
                      <Stack spacing={1.4} sx={{ width: '100%' }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {s.logo
                            ? <Avatar src={s.logo} variant="rounded" sx={{ width: 52, height: 52 }} />
                            : <Avatar variant="rounded" sx={{ width: 52, height: 52 }}>
                                <LogoIcon />
                              </Avatar>}
                          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight={800} noWrap>
                              {s.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {s.code}{s.region ? ` · ${s.region}` : ''}
                            </Typography>
                          </Box>
                        </Stack>

                        {s.motto && (
                          <Typography variant="caption" color="text.secondary"
                            sx={{ fontStyle: 'italic' }} noWrap>
                            “{s.motto}”
                          </Typography>
                        )}

                        <Stack direction="row" spacing={0.7} alignItems="center" flexWrap="wrap">
                          {isMain && (
                            <Chip size="small" icon={<HomeIcon sx={{ fontSize: 14 }} />}
                              label="Main school" variant="outlined"
                              sx={{ height: 22, fontSize: 10.5, fontWeight: 700 }} />
                          )}
                          {isCurrent
                            ? <Chip size="small" color="primary"
                                icon={<CurrentIcon sx={{ fontSize: 14 }} />}
                                label="You are here"
                                sx={{ height: 22, fontSize: 10.5, fontWeight: 700 }} />
                            : <Chip size="small" color="default"
                                icon={<EnterIcon sx={{ fontSize: 14 }} />}
                                label="Click to open"
                                sx={{ height: 22, fontSize: 10.5, fontWeight: 600 }} />}
                          {!s.provisioned_at && !isMain && (
                            <Chip size="small" color="warning" label="Not built yet"
                              sx={{ height: 22, fontSize: 10.5, fontWeight: 700 }} />
                          )}
                        </Stack>
                      </Stack>
                    </CardActionArea>
                  </Tooltip>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Alert severity="info" variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
        Opening a school reloads the app so nothing from the previous school stays
        on screen. The school you are in is always shown in the top bar, and this
        page — or that top-bar picker — brings you back.
      </Alert>
    </Box>
  );
}
